import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/** Asset knowledge-base scope. When set, every user message ChatPanel sends
 *  carries this in requestBody.context.items, so the agent can route to the
 *  retrieve_asset_kb MCP tool. Sticky until the user explicitly clears it
 *  (chip × in the ChatPanel toolbar) or rebinds to another asset. */
export interface AssetScope {
  assetId: string;
  assetName: string;
}

/** Use Case knowledge-base scope — the UC parallel of AssetScope. When set, every
 *  user message carries the UC's solutionMapId + name in context.items so the agent
 *  can route to the retrieve_uc_kb MCP tool. Asset scope and UC scope are MUTUALLY
 *  EXCLUSIVE (one KB focus at a time) — binding one clears the other. */
export interface UcScope {
  solutionMapId: number;
  ucName: string;
}

/** Storyline knowledge-base scope — the Storyline parallel of Asset/UcScope. When set,
 *  every user message carries the storyline's id + name in context.items so the agent
 *  can route to the retrieve_storyline_kb MCP tool. All three scopes (asset / uc /
 *  storyline) are MUTUALLY EXCLUSIVE — binding one clears the other two. */
export interface StorylineScope {
  storylineId: string;
  storylineName: string;
}

/** The relevance query the Create Wave panel currently has applied.
 *
 *  NOT a KB focus, so deliberately NOT mutually exclusive with the three scopes
 *  above — a user can be looking at an Asset's KB and filtering the wave panel at
 *  the same time.
 *
 *  Exists because the panel is the source of truth for the query and the agent
 *  cannot see it. Without this, a user who dismisses a filter chip has removed it
 *  from the panel but not from the conversation, so the next spec the agent writes
 *  puts it back — the filter returns with nothing to explain why. */
export interface WaveQueryScope {
  motionId: string;
  /** The live spec, serialised. The agent's job is to emit one of these, so
   *  handing it the current one is directly usable rather than merely
   *  descriptive. */
  specJson: string;
}

interface ChatPanelContextValue {
  chatCollapsed: boolean;
  toggleChat: () => void;
  chatFullscreen: boolean;
  toggleFullscreen: () => void;
  exitFullscreen: () => void;
  pendingPrompt: string;
  sendPrompt: (text: string) => void;
  clearPendingPrompt: () => void;
  draftPrompt: string;
  prefillPrompt: (text: string) => void;
  clearDraftPrompt: () => void;
  chatBusy: boolean;
  setChatBusy: (busy: boolean) => void;
  chatQueueFull: boolean;
  setChatQueueFull: (full: boolean) => void;
  assetScope: AssetScope | null;
  setAssetScope: (scope: AssetScope | null) => void;
  ucScope: UcScope | null;
  setUcScope: (scope: UcScope | null) => void;
  storylineScope: StorylineScope | null;
  setStorylineScope: (scope: StorylineScope | null) => void;
  waveQueryScope: WaveQueryScope | null;
  setWaveQueryScope: (scope: WaveQueryScope | null) => void;
}

// Exported so tests can inject a spy value (e.g. a vi.fn() sendPrompt) via
// <ChatPanelContext.Provider>; production code uses ChatPanelProvider below.
export const ChatPanelContext = createContext<ChatPanelContextValue>({
  chatCollapsed: false,
  toggleChat: () => {},
  chatFullscreen: false,
  toggleFullscreen: () => {},
  exitFullscreen: () => {},
  pendingPrompt: '',
  sendPrompt: () => {},
  clearPendingPrompt: () => {},
  draftPrompt: '',
  prefillPrompt: () => {},
  clearDraftPrompt: () => {},
  chatBusy: false,
  setChatBusy: () => {},
  chatQueueFull: false,
  setChatQueueFull: () => {},
  assetScope: null,
  setAssetScope: () => {},
  ucScope: null,
  setUcScope: () => {},
  storylineScope: null,
  waveQueryScope: null,
  setWaveQueryScope: () => {},
  setStorylineScope: () => {},
});

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatFullscreen, setChatFullscreen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [draftPrompt, setDraftPrompt] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatQueueFull, setChatQueueFull] = useState(false);
  const [assetScope, setAssetScopeState] = useState<AssetScope | null>(null);
  const [ucScope, setUcScopeState] = useState<UcScope | null>(null);
  const [storylineScope, setStorylineScopeState] = useState<StorylineScope | null>(null);
  // Plain setter: no mutual exclusion, see WaveQueryScope.
  const [waveQueryScope, setWaveQueryScope] = useState<WaveQueryScope | null>(null);

  // Asset / UC / Storyline scopes are mutually exclusive (one KB focus at a time):
  // binding one clears the other two, so context.items only ever carries a single
  // scope and the agent knows which retrieve_*_kb tool to call. Clearing (null)
  // leaves the others untouched (they're already null when one is set).
  const setAssetScope = useCallback((scope: AssetScope | null) => {
    setAssetScopeState(scope);
    if (scope) { setUcScopeState(null); setStorylineScopeState(null); }
  }, []);
  const setUcScope = useCallback((scope: UcScope | null) => {
    setUcScopeState(scope);
    if (scope) { setAssetScopeState(null); setStorylineScopeState(null); }
  }, []);
  const setStorylineScope = useCallback((scope: StorylineScope | null) => {
    setStorylineScopeState(scope);
    if (scope) { setAssetScopeState(null); setUcScopeState(null); }
  }, []);

  const toggleChat = useCallback(() => {
    setChatCollapsed(prev => {
      if (!prev && chatFullscreen) setChatFullscreen(false);
      return !prev;
    });
  }, [chatFullscreen]);

  const toggleFullscreen = useCallback(() => {
    setChatFullscreen(prev => {
      if (!prev && chatCollapsed) setChatCollapsed(false);
      return !prev;
    });
  }, [chatCollapsed]);

  const exitFullscreen = useCallback(() => setChatFullscreen(false), []);

  const sendPrompt = useCallback((text: string) => {
    setPendingPrompt(text);
    if (chatCollapsed) setChatCollapsed(false);
  }, [chatCollapsed]);

  const clearPendingPrompt = useCallback(() => setPendingPrompt(''), []);

  const prefillPrompt = useCallback((text: string) => {
    setDraftPrompt(text);
    if (chatCollapsed) setChatCollapsed(false);
  }, [chatCollapsed]);

  const clearDraftPrompt = useCallback(() => setDraftPrompt(''), []);

  return (
    <ChatPanelContext.Provider value={{ chatCollapsed, toggleChat, chatFullscreen, toggleFullscreen, exitFullscreen, pendingPrompt, sendPrompt, clearPendingPrompt, draftPrompt, prefillPrompt, clearDraftPrompt, chatBusy, setChatBusy, chatQueueFull, setChatQueueFull, assetScope, setAssetScope, ucScope, setUcScope, storylineScope, setStorylineScope, waveQueryScope, setWaveQueryScope }}>
      {children}
    </ChatPanelContext.Provider>
  );
}

export function useChatPanel() {
  return useContext(ChatPanelContext);
}
